import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Brain, Eye, MessageSquarePlus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { TEAL } from "@/lib/copilot";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader } from "@/components/common";
import ChatView from "@/components/copilot/ChatView";

export function CopilotTabs({ active, canSeeKids }) {
  const { hub } = useHub();
  const tabs = [["chat", "Chats", "copilot"], ["memory", "Memory & documents", "copilot/memory"], ...(canSeeKids ? [["kids", "Kids' chats", "copilot/kids"]] : [])];
  return (
    <div className="mb-5 flex flex-wrap gap-1.5" data-testid="copilot-tabs">
      {tabs.map(([id, label, path]) => (
        <Link key={id} to={`/h/${hub.id}/${path}`} className="inline-flex min-h-[44px] items-center rounded-full px-4 text-sm font-bold transition-colors"
          style={active === id ? { background: `rgba(${TEAL},0.18)`, color: `rgb(${TEAL})` } : { color: "rgb(var(--q-muted))" }} data-testid={`copilot-tab-${id}`}>{label}</Link>
      ))}
    </div>
  );
}

function ConvItem({ c, active, onOpen, onRename, onDelete }) {
  return (
    <li className="group flex items-center gap-1 rounded-xl pr-1 transition-colors" style={active ? { background: `rgba(${TEAL},0.14)` } : undefined}>
      <button className="min-h-[44px] min-w-0 flex-1 truncate px-3 text-left text-sm font-semibold text-q-text" onClick={onOpen} data-testid={`conv-item-${c.id}`}>{c.title}</button>
      <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-q-faint hover:text-q-text" aria-label="Rename" onClick={onRename} data-testid={`conv-rename-${c.id}`}><Pencil className="h-3.5 w-3.5" /></button>
      <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-q-faint hover:text-red-300" aria-label="Delete" onClick={onDelete} data-testid={`conv-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </li>
  );
}

export default function Copilot() {
  const { hub } = useHub();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const cid = sp.get("c");
  const [viewKey, setViewKey] = useState(cid || "new");
  const base = `/organizations/${hub.id}/copilot/conversations`;
  const load = useCallback(() => api.get(base).then(({ data: d }) => setData(d)).catch((e) => toast.error(errMsg(e))), [base]);
  useEffect(() => { load(); }, [load]);
  const open = (id) => { setViewKey(id || `new-${Date.now()}`); setSp(id ? { c: id } : {}); };
  const ensure = async () => { const { data: c } = await api.post(base, {}); setSp({ c: c.id }, { replace: true }); setTimeout(load, 1500); return c.id; };
  const rename = async (c) => {
    const title = window.prompt("Rename chat", c.title);
    if (title === null) return;
    try { await api.patch(`${base}/${c.id}`, { title }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete “${c.title}”?`)) return;
    await api.delete(`${base}/${c.id}`).catch(() => {});
    if (cid === c.id) open(null);
    load();
  };
  if (!data) return <Loader />;
  return (
    <div data-testid="copilot-page">
      <PageHeader eyebrow="Copilot" title="Ask Copilot" rgb={TEAL} testid="copilot-title" />
      <CopilotTabs active="chat" canSeeKids={data.can_see_kids} />
      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <aside className="q-card h-fit p-2" data-testid="conv-list">
          <button className="q-btn q-btn-secondary mb-2 w-full min-h-[44px]" onClick={() => open(null)} data-testid="conv-new-btn"><MessageSquarePlus className="h-4 w-4" /> New chat</button>
          {data.conversations.length === 0 && <p className="px-3 py-4 text-xs text-q-faint">No chats yet.</p>}
          <ul className="max-h-[50vh] space-y-0.5 overflow-y-auto">
            {data.conversations.map((c) => <ConvItem key={c.id} c={c} active={c.id === cid} onOpen={() => open(c.id)} onRename={() => rename(c)} onDelete={() => remove(c)} />)}
          </ul>
        </aside>
        <section className="q-card flex h-[calc(100vh-260px)] min-h-[460px] flex-col">
          <ChatView key={viewKey} hubId={hub.id} cid={cid} onEnsure={ensure} suggestions={data.suggestions} isKid={data.is_kid} />
        </section>
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-xs text-q-faint"><Brain className="h-3.5 w-3.5" /> Copilot uses your hub data with your own permissions. Your chats are private to you{data.is_kid ? " and your grown-ups" : ""}.</p>
    </div>
  );
}

export function KidsChats() {
  const { hub } = useHub();
  const [data, setData] = useState(null);
  const [cid, setCid] = useState(null);
  useEffect(() => { api.get(`/organizations/${hub.id}/copilot/kids-chats`).then(({ data: d }) => setData(d)).catch((e) => setData({ error: errMsg(e) })); }, [hub.id]);
  if (!data) return <Loader />;
  return (
    <div data-testid="kids-chats-page">
      <PageHeader eyebrow="Copilot" title="Kids' chats" rgb={TEAL} testid="kids-chats-title" />
      <CopilotTabs active="kids" canSeeKids />
      {data.error ? <p className="q-error" data-testid="kids-chats-error">{data.error}</p> : (
        <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
          <ul className="q-card h-fit space-y-0.5 p-2" data-testid="kids-chats-list">
            {data.conversations.length === 0 && <li className="px-3 py-4 text-xs text-q-faint">No kids' chats yet.</li>}
            {data.conversations.map((c) => (
              <li key={c.id}><button className="flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 text-left text-sm" style={c.id === cid ? { background: `rgba(${TEAL},0.14)` } : undefined}
                onClick={() => setCid(c.id)} data-testid={`kids-chat-${c.id}`}><span className="font-bold text-q-text">{c.owner_name}</span><span className="truncate text-q-muted">{c.title}</span></button></li>
            ))}
          </ul>
          <section className="q-card flex h-[calc(100vh-260px)] min-h-[420px] flex-col">
            <p className="mb-3 flex items-center gap-1.5 text-xs font-bold text-q-faint" data-testid="kids-chats-readonly"><Eye className="h-3.5 w-3.5" /> Read-only</p>
            {cid ? <ChatView key={cid} hubId={hub.id} cid={cid} readOnly /> : <p className="text-sm text-q-muted">Pick a chat to read it.</p>}
          </section>
        </div>
      )}
    </div>
  );
}
